import {
  OutputDocument,
  OutputDocumentVersion,
  OutputDocumentChatMessage,
  SandboxGenerationRun,
} from "@/types";
import {
  activateOutputDocumentVersion,
  chatEditOutputDocument,
  downloadOutputDocumentVersion,
  downloadOutputDocumentVersionPreview,
  getOutputDocument,
  getOutputDocumentChatMessages,
  getOutputDocumentVersions,
  getSandboxGenerationRun,
  previewOutputDocumentVersion,
  regenerateOutputDocument,
  updateOutputDocumentVersionStatus,
  uploadOutputDocumentVersion,
} from "@/utils/apiCalls";
import { useAuthInfo } from "@propelauth/react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  ChevronDown,
  Download,
  FileText,
  MessageSquare,
  Sparkles,
  Upload,
  Check,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ReloadIcon } from "@radix-ui/react-icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import PdfViewer from "@/components/pdfViewer/PDFViewer";
import { loadAndFormatDate } from "@/utils/dateFormat";
import { StatusBadge } from "@/components/Status";
import { Link } from "react-router-dom";

const SELECTED_CONTEXT_MAX_CHARS = 2000;

const formatSelectedContextText = (text: string) => {
  if (text.length <= SELECTED_CONTEXT_MAX_CHARS) return text;
  return `${text.slice(0, SELECTED_CONTEXT_MAX_CHARS).trim()}...`;
};

const ActionTooltip = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex">{children}</span>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

const formatVersionStatus = (status: OutputDocumentVersion["status"]) =>
  status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

type VersionSourceKind = "auto" | "uploaded";

const getVersionSourceKind = (
  source: OutputDocumentVersion["source"]
): VersionSourceKind => (source === "user_uploaded" ? "uploaded" : "auto");

const versionSourceBadgeClassName: Record<VersionSourceKind, string> = {
  auto: "border-sky-200 bg-sky-50 text-sky-700",
  uploaded: "border-amber-200 bg-amber-50 text-amber-800",
};

const VersionSourceBadge = ({
  source,
}: {
  source: OutputDocumentVersion["source"];
}) => {
  const kind = getVersionSourceKind(source);
  return (
    <Badge
      variant="outline"
      className={cn(
        "rounded-full px-2 py-0 text-[10px] font-medium uppercase tracking-wide",
        versionSourceBadgeClassName[kind]
      )}
    >
      {kind}
    </Badge>
  );
};

const VersionSelectorLabel = ({
  version,
  latestVersionNumber,
  showTimestamp = false,
}: {
  version: OutputDocumentVersion;
  latestVersionNumber?: number;
  showTimestamp?: boolean;
}) => {
  const isLatest =
    latestVersionNumber !== undefined &&
    version.version === latestVersionNumber;

  return (
    <span className="flex min-w-0 items-center gap-2">
      <span>v{version.version}</span>
      <VersionSourceBadge source={version.source} />
      {isLatest && (
        <Badge
          variant="outline"
          className="rounded-full border-violet-200 bg-violet-50 px-2 py-0 text-[10px] font-medium uppercase tracking-wide text-violet-700"
        >
          latest
        </Badge>
      )}
      {version.status === "finalized" && (
        <Badge
          variant="outline"
          className="rounded-full border-emerald-200 bg-emerald-50 px-2 py-0 text-[10px] font-medium uppercase tracking-wide text-emerald-700"
        >
          final
        </Badge>
      )}
      {showTimestamp && (
        <span className="truncate text-xs text-slate-500">
          {loadAndFormatDate(version.created_at)}
        </span>
      )}
    </span>
  );
};

const CHAT_EDIT_STEPS = [
  {
    id: "preparing_workspace",
    label: "Preparing workspace",
    description: "Loading records, skills, and prior examples",
  },
  {
    id: "running_agent",
    label: "Running AI edit",
    description: "Applying your instructions in the sandbox",
  },
  {
    id: "saving_version",
    label: "Saving new version",
    description: "Creating the DOCX and PDF preview",
  },
] as const;

const getChatEditStepState = (
  stepId: (typeof CHAT_EDIT_STEPS)[number]["id"],
  progress: string | undefined,
  status: SandboxGenerationRun["status"] | undefined
) => {
  const progressIndex = CHAT_EDIT_STEPS.findIndex(
    (step) => step.id === progress
  );
  const stepIndex = CHAT_EDIT_STEPS.findIndex((step) => step.id === stepId);
  const activeIndex =
    progress === "queued" || progressIndex === -1 ? 0 : progressIndex;

  if (status === "completed") {
    return "complete";
  }
  if (status === "failed") {
    if (stepIndex < activeIndex) return "complete";
    if (stepIndex === activeIndex) return "failed";
    return "pending";
  }
  if (stepIndex < activeIndex) return "complete";
  if (stepIndex === activeIndex) return "active";
  return "pending";
};

type ChatHistoryEntry = OutputDocumentChatMessage;

const ChatEditProgress = ({ run }: { run: SandboxGenerationRun | null }) => {
  const progress = run?.sandbox_metadata?.progress;

  return (
    <div className="rounded-lg border bg-slate-50 p-4">
      <div className="space-y-3">
        {CHAT_EDIT_STEPS.map((step) => {
          const state = getChatEditStepState(step.id, progress, run?.status);
          return (
            <div key={step.id} className="flex items-start gap-3">
              <div
                className={cn(
                  "mt-0.5 flex h-6 w-6 items-center justify-center rounded-full border",
                  state === "complete" &&
                    "border-emerald-200 bg-emerald-50 text-emerald-700",
                  state === "active" && "border-sky-200 bg-sky-50 text-sky-700",
                  state === "failed" && "border-red-200 bg-red-50 text-red-700",
                  state === "pending" &&
                    "border-slate-200 bg-white text-slate-400"
                )}
              >
                {state === "complete" ? (
                  <Check className="h-3.5 w-3.5" />
                ) : state === "active" ? (
                  <ReloadIcon className="h-3.5 w-3.5 animate-spin" />
                ) : state === "failed" ? (
                  <span className="text-xs font-semibold">!</span>
                ) : (
                  <span className="h-2 w-2 rounded-full bg-slate-300" />
                )}
              </div>
              <div className="min-w-0">
                <p
                  className={cn(
                    "text-sm font-medium",
                    state === "pending" ? "text-slate-400" : "text-slate-900"
                  )}
                >
                  {step.label}
                </p>
                <p className="text-xs text-slate-500">{step.description}</p>
              </div>
            </div>
          );
        })}
      </div>
      {run?.status === "failed" && run.error && (
        <p className="mt-3 text-sm text-red-600">{run.error}</p>
      )}
    </div>
  );
};

const ChatHistory = ({
  messages,
  chatLoading,
  chatRun,
}: {
  messages: ChatHistoryEntry[];
  chatLoading: boolean;
  chatRun: SandboxGenerationRun | null;
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages.length, chatLoading, chatRun?.sandbox_metadata?.progress]);

  const hasContent = messages.length > 0 || chatLoading;

  if (!hasContent) {
    return null;
  }

  return (
    <div
      ref={scrollRef}
      className="min-h-[220px] max-h-[42vh] overflow-y-auto rounded-lg border bg-slate-50 p-3 lg:max-h-none lg:flex-1"
    >
      <div className="space-y-3">
        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              "flex",
              message.role === "user" ? "justify-end" : "justify-start"
            )}
          >
            <div
              className={cn(
                "max-w-[92%] rounded-2xl px-3 py-2 text-sm shadow-sm",
                message.role === "user"
                  ? "bg-sky-600 text-white"
                  : "border bg-white text-slate-700"
              )}
            >
              {message.content}
            </div>
          </div>
        ))}
        {chatLoading && <ChatEditProgress run={chatRun} />}
      </div>
    </div>
  );
};

const ChatEditPanel = ({
  activeVersion,
  chatInstructions,
  setChatInstructions,
  selectedContextText,
  onClearSelectedContext,
  chatLoading,
  chatRun,
  chatMessages,
  latestVersionNumber,
  onSubmit,
}: {
  activeVersion: OutputDocumentVersion | null;
  chatInstructions: string;
  setChatInstructions: (value: string) => void;
  selectedContextText: string;
  onClearSelectedContext: () => void;
  chatLoading: boolean;
  chatRun: SandboxGenerationRun | null;
  chatMessages: ChatHistoryEntry[];
  latestVersionNumber?: number;
  onSubmit: () => void;
}) => (
  <aside
    aria-label="AI edit chat sidebar"
    className="flex h-full min-h-[520px] flex-col overflow-hidden rounded-xl border bg-white shadow-sm lg:min-h-[calc(100vh-8.5rem)]"
  >
    <div className="border-b px-5 py-4">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-sky-600" />
        <p className="text-sm font-medium text-slate-900">Chat</p>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        {activeVersion
          ? "Edit the document by telling AI what to fix."
          : "Select a version, then edit the document by telling AI what to fix."}
      </p>
    </div>
    <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 py-4">
      <ChatHistory
        messages={chatMessages}
        chatLoading={chatLoading}
        chatRun={chatRun}
      />
      <div className="space-y-2">
        {selectedContextText && (
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-medium uppercase tracking-wide text-sky-700">
                Selected document context
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-sky-700 hover:bg-sky-100"
                onClick={onClearSelectedContext}
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </Button>
            </div>
            <p className="mt-2 max-h-24 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-slate-700">
              {selectedContextText}
            </p>
          </div>
        )}
        <Textarea
          id="ai-edit-chat-message"
          value={chatInstructions}
          onChange={(event) => setChatInstructions(event.target.value)}
          placeholder="Type a chat message, e.g. Tighten the causation section and add a clearer summary of the MRI findings."
          disabled={!activeVersion || chatLoading}
          rows={4}
          className="resize-none bg-slate-50"
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              (event.metaKey || event.ctrlKey) &&
              !chatLoading &&
              activeVersion &&
              chatInstructions.trim()
            ) {
              event.preventDefault();
              onSubmit();
            }
          }}
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          Press Cmd/Ctrl + Enter to send. Replies save as new versions.
        </p>
        <Button
          disabled={!activeVersion || chatLoading || !chatInstructions.trim()}
          onClick={onSubmit}
        >
          {chatLoading ? (
            <ReloadIcon className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          Send Message
        </Button>
      </div>
    </div>
  </aside>
);

export const DocumentDisplay = (props: {
  caseId: string;
  document: OutputDocument;
  setDocument: React.Dispatch<React.SetStateAction<OutputDocument | null>>;
}) => {
  const authInfo = useAuthInfo();
  const [versions, setVersions] = useState<OutputDocumentVersion[]>([]);
  const [activeVersion, setActiveVersion] =
    useState<OutputDocumentVersion | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [versionLoading, setVersionLoading] = useState(false);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [regenerateInstructions, setRegenerateInstructions] = useState("");
  const [finalizingLoading, setFinalizingLoading] = useState(false);
  const [chatInstructions, setChatInstructions] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatRun, setChatRun] = useState<SandboxGenerationRun | null>(null);
  const [activeChatRunId, setActiveChatRunId] = useState<string | null>(null);
  const [selectedContextText, setSelectedContextText] = useState("");
  const [chatMessages, setChatMessages] = useState<OutputDocumentChatMessage[]>(
    []
  );
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const applyVersions = (nextVersions: OutputDocumentVersion[]) => {
    setVersions(nextVersions);
    setActiveVersion(
      nextVersions.find((version) => version.active) ?? nextVersions[0] ?? null
    );
  };

  const loadVersions = async () => {
    const response = await getOutputDocumentVersions(
      props.document.id,
      authInfo.accessToken ?? null
    );
    applyVersions(response ?? []);
  };

  const loadChatMessages = async () => {
    const response = await getOutputDocumentChatMessages(
      props.document.id,
      authInfo.accessToken ?? null
    );
    setChatMessages(response ?? []);
  };

  useEffect(() => {
    loadVersions();
    loadChatMessages();
  }, [props.document.id, authInfo.accessToken]);

  useEffect(() => {
    const loadPreview = async () => {
      if (!activeVersion?.pdf_preview_storage_path) {
        setPreviewUrl(null);
        return;
      }
      const url = await previewOutputDocumentVersion(
        props.document.id,
        activeVersion.id,
        authInfo.accessToken ?? null
      );
      setPreviewUrl(url);
    };
    loadPreview();
  }, [activeVersion?.id, props.document.id, authInfo.accessToken]);

  useEffect(() => {
    setSelectedContextText("");
  }, [activeVersion?.id]);

  useEffect(() => {
    if (props.document.status !== "generating") return;

    let cancelled = false;
    const pollGeneratedDocument = async () => {
      try {
        const [documentResponse, versionResponse] = await Promise.all([
          getOutputDocument(props.document.id, authInfo.accessToken ?? null),
          getOutputDocumentVersions(
            props.document.id,
            authInfo.accessToken ?? null
          ),
        ]);
        if (cancelled) return;
        if (documentResponse?.document) {
          props.setDocument(documentResponse.document);
        }
        applyVersions(versionResponse ?? []);
      } catch (error) {
        console.error(error);
      }
    };

    pollGeneratedDocument();
    const intervalId = window.setInterval(pollGeneratedDocument, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [props.document.id, props.document.status, authInfo.accessToken]);

  useEffect(() => {
    if (!activeChatRunId) return;

    let cancelled = false;
    const pollChatRun = async () => {
      const run = await getSandboxGenerationRun(
        props.document.id,
        activeChatRunId,
        authInfo.accessToken ?? null
      );
      if (cancelled || !run) return;

      setChatRun(run);

      if (run.status === "completed") {
        setChatLoading(false);
        setActiveChatRunId(null);
        setChatInstructions("");
        toast.success("Created a new version from your edit");
        await Promise.all([loadVersions(), loadChatMessages()]);
      } else if (run.status === "failed") {
        setChatLoading(false);
        setActiveChatRunId(null);
        await loadChatMessages();
        toast.error(run.error ?? "Failed to edit document");
      }
    };

    pollChatRun();
    const intervalId = window.setInterval(pollChatRun, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeChatRunId, props.document.id, authInfo.accessToken]);

  const handleVersionUpload = async (file: File | null) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".docx")) {
      toast.error("Please upload a DOCX file");
      return;
    }
    setVersionLoading(true);
    const response = await uploadOutputDocumentVersion(
      props.document.id,
      file,
      "draft",
      authInfo.accessToken ?? null
    );
    setVersionLoading(false);
    if (!response) {
      toast.error("Failed to upload revised DOCX");
      return;
    }
    toast.success("Uploaded revised DOCX");
    await loadVersions();
  };

  const handleRegenerate = async (userInstructions?: string) => {
    setRegenerateLoading(true);
    const response = await regenerateOutputDocument(
      props.document.id,
      authInfo.accessToken ?? null,
      userInstructions
    );
    setRegenerateLoading(false);
    if (!response) {
      toast.error("Failed to start regeneration");
      return;
    }
    if (userInstructions?.trim()) {
      setRegenerateInstructions("");
    }
    toast.success("Regeneration started");
    props.setDocument(response);
    await loadVersions();
  };

  const handleDownloadDocx = () => {
    if (!activeVersion) return;
    downloadOutputDocumentVersion(
      props.document.id,
      activeVersion.id,
      authInfo.accessToken ?? null
    );
  };

  const handleDownloadPdf = () => {
    if (!activeVersion?.pdf_preview_storage_path) {
      toast.error("PDF preview is not available for this version");
      return;
    }
    downloadOutputDocumentVersionPreview(
      props.document.id,
      activeVersion.id,
      authInfo.accessToken ?? null
    );
  };

  const handleToggleFinalStatus = async () => {
    if (!activeVersion) return;
    const nextStatus =
      activeVersion.status === "finalized" ? "draft" : "finalized";
    setFinalizingLoading(true);
    const updated = await updateOutputDocumentVersionStatus(
      props.document.id,
      activeVersion.id,
      nextStatus,
      authInfo.accessToken ?? null
    );
    setFinalizingLoading(false);
    if (updated) {
      toast.success(
        nextStatus === "finalized"
          ? "Version marked final"
          : "Version unmarked as final"
      );
      await loadVersions();
    } else {
      toast.error(
        nextStatus === "finalized"
          ? "Failed to mark version final"
          : "Failed to unmark version as final"
      );
    }
  };

  const handleChatEdit = async () => {
    if (!activeVersion || !chatInstructions.trim()) return;
    const trimmedInstructions = chatInstructions.trim();
    const instructionsWithContext = selectedContextText
      ? `Selected document context:\n${selectedContextText}\n\nUser message:\n${trimmedInstructions}`
      : trimmedInstructions;

    setChatLoading(true);
    setChatRun(null);
    const response = await chatEditOutputDocument(
      props.document.id,
      instructionsWithContext,
      authInfo.accessToken ?? null
    );
    if (!response?.run_id) {
      setChatLoading(false);
      toast.error("Failed to start document edit");
      return;
    }
    setChatInstructions("");
    setSelectedContextText("");
    await loadChatMessages();
    setActiveChatRunId(response.run_id);
  };

  const hasDocxVersion = versions.length > 0 && activeVersion !== null;
  const isGenerating = props.document.status === "generating";
  const isFailed = props.document.status === "failed";
  const isVersionFinal = activeVersion?.status === "finalized";
  const latestVersionNumber =
    versions.length > 0
      ? Math.max(...versions.map((version) => version.version))
      : undefined;

  const chatPanelProps = {
    activeVersion,
    chatInstructions,
    setChatInstructions,
    selectedContextText,
    onClearSelectedContext: () => setSelectedContextText(""),
    chatLoading,
    chatRun,
    chatMessages,
    latestVersionNumber,
    onSubmit: handleChatEdit,
  };

  const previewPanel = (
    <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
      <div className="border-b px-5 py-4">
        <p className="text-sm font-medium text-slate-900">PDF Preview</p>
        <p className="mt-1 text-sm text-slate-500">
          Review the selected version before downloading, uploading a revision,
          or marking it final.
        </p>
      </div>
      <div className="bg-slate-100 px-4 py-6 sm:px-8">
        <div className="mx-auto max-w-4xl bg-white shadow-md">
          <div className="space-y-4 px-4 py-8 sm:px-12 lg:px-20">
            {previewUrl ? (
              <PdfViewer
                fileUrl={previewUrl}
                enableTextSelection
                onTextSelection={(text) =>
                  setSelectedContextText(formatSelectedContextText(text))
                }
              />
            ) : (
              <div className="rounded-lg border border-dashed p-10 text-center text-sm text-slate-500">
                No PDF preview is available for this document version.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="sticky top-0 z-20 border-b bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-lg font-semibold text-slate-950">
                {props.document.document_type_name}
              </h1>
              <StatusBadge status={props.document.status} />
              {activeVersion && (
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                    isVersionFinal
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {isVersionFinal
                    ? "Final"
                    : formatVersionStatus(activeVersion.status)}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {activeVersion ? (
                <span className="inline-flex items-center gap-2">
                  Previewing
                  <VersionSelectorLabel
                    version={activeVersion}
                    latestVersionNumber={latestVersionNumber}
                  />
                </span>
              ) : (
                "Upload a DOCX version to create a preview."
              )}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={activeVersion?.id}
              onValueChange={async (versionId) => {
                const selected = versions.find((v) => v.id === versionId);
                setActiveVersion(selected ?? null);
                if (selected && !selected.active) {
                  await activateOutputDocumentVersion(
                    props.document.id,
                    selected.id,
                    authInfo.accessToken ?? null
                  );
                  await loadVersions();
                }
              }}
              disabled={!hasDocxVersion}
            >
              <SelectTrigger className="w-[360px] bg-white">
                <SelectValue placeholder="Select version">
                  {activeVersion ? (
                    <VersionSelectorLabel
                      version={activeVersion}
                      latestVersionNumber={latestVersionNumber}
                      showTimestamp
                    />
                  ) : (
                    "Select version"
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {versions.map((version) => (
                  <SelectItem key={version.id} value={version.id}>
                    <VersionSelectorLabel
                      version={version}
                      latestVersionNumber={latestVersionNumber}
                      showTimestamp
                    />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="inline-flex shadow-sm">
              <ActionTooltip label="Download DOCX">
                <Button
                  aria-label="Download DOCX"
                  size="icon"
                  className="rounded-r-none"
                  disabled={!activeVersion}
                  onClick={handleDownloadDocx}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </ActionTooltip>
              <DropdownMenu>
                <ActionTooltip label="Choose download format">
                  <DropdownMenuTrigger asChild>
                    <Button
                      aria-label="Choose download format"
                      size="icon"
                      className="-ml-px rounded-l-none border-l border-primary-foreground/20"
                      disabled={!activeVersion}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </ActionTooltip>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Download as</DropdownMenuLabel>
                  <DropdownMenuItem onClick={handleDownloadDocx}>
                    <FileText className="mr-2 h-4 w-4" />
                    DOCX
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!activeVersion?.pdf_preview_storage_path}
                    onClick={handleDownloadPdf}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <input
              ref={uploadInputRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(event) => {
                handleVersionUpload(event.target.files?.[0] ?? null);
                event.currentTarget.value = "";
              }}
            />
            <ActionTooltip label="Upload new version">
              <Button
                aria-label="Upload new version"
                variant="outline"
                size="icon"
                disabled={versionLoading}
                onClick={() => uploadInputRef.current?.click()}
              >
                {versionLoading ? (
                  <ReloadIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
              </Button>
            </ActionTooltip>

            <div className="inline-flex shadow-sm">
              <ActionTooltip label="Regenerate document">
                <Button
                  aria-label="Regenerate document"
                  variant="outline"
                  size="icon"
                  className="rounded-r-none"
                  disabled={isGenerating || regenerateLoading}
                  onClick={() => handleRegenerate()}
                >
                  {isGenerating || regenerateLoading ? (
                    <ReloadIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                </Button>
              </ActionTooltip>
              <DropdownMenu>
                <ActionTooltip label="Regenerate with instructions">
                  <DropdownMenuTrigger asChild>
                    <Button
                      aria-label="Regenerate with instructions"
                      variant="outline"
                      size="icon"
                      className="-ml-px rounded-l-none"
                      disabled={isGenerating || regenerateLoading}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </ActionTooltip>
                <DropdownMenuContent align="end" className="w-96 space-y-3 p-3">
                  <DropdownMenuLabel className="px-0">
                    Regeneration instructions
                  </DropdownMenuLabel>
                  <p className="text-xs text-slate-500">
                    Tell the model what to change. By default, regeneration
                    preserves existing edits and unrelated sections.
                  </p>
                  <Textarea
                    value={regenerateInstructions}
                    onChange={(event) =>
                      setRegenerateInstructions(event.target.value)
                    }
                    placeholder="Example: Regenerate only the RECORD REVIEW section using the newly OCR'd records. Preserve Identifying Data and all other sections."
                    rows={5}
                    className="resize-none"
                  />
                  <Button
                    className="w-full"
                    disabled={!regenerateInstructions.trim()}
                    onClick={() =>
                      handleRegenerate(regenerateInstructions.trim())
                    }
                  >
                    <Sparkles className="h-4 w-4" />
                    Regenerate with instructions
                  </Button>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <ActionTooltip
              label={
                isVersionFinal ? "Unmark as final" : "Mark version as final"
              }
            >
              <Button
                aria-label={
                  isVersionFinal ? "Unmark as final" : "Mark version as final"
                }
                variant={isVersionFinal ? "secondary" : "outline"}
                size="icon"
                disabled={!activeVersion || finalizingLoading}
                onClick={handleToggleFinalStatus}
              >
                {finalizingLoading ? (
                  <ReloadIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
              </Button>
            </ActionTooltip>
          </div>
        </div>
      </div>

      <div className="px-4 py-6">
        {isFailed && (
          <div className="mx-auto mb-4 flex max-w-7xl flex-col gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Generation failed before producing an acceptable draft. Review the
              source documents and re-process any failed or unreadable files
              before regenerating.
            </span>
            <Button asChild size="sm" variant="outline">
              <Link to={`/case/${props.caseId}#input-docs`}>
                Review source documents
              </Link>
            </Button>
          </div>
        )}
        <div className="mx-auto hidden max-w-7xl lg:grid lg:grid-cols-[minmax(0,1.45fr)_minmax(360px,420px)] lg:items-start lg:gap-6">
          {previewPanel}
          <div className="lg:sticky lg:top-24">
            <ChatEditPanel {...chatPanelProps} />
          </div>
        </div>

        <div className="mx-auto max-w-5xl space-y-6 lg:hidden">
          {previewPanel}
          <ChatEditPanel {...chatPanelProps} />
        </div>
      </div>
    </div>
  );
};
